import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingPaintersCapeTownMainPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Cape Town Painters - Professional Painting Services | Residential & Commercial Experts',
    description: 'Premier painting services throughout Cape Town. Specializing in residential, commercial, and industrial painting with over 20 years of experience. Quality craftsmanship, competitive pricing, and exceptional customer service guaranteed.',
    keywords: 'painters Cape Town, professional painters, house painting Cape Town, commercial painting, residential painting, interior painting, exterior painting, roof painting',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Cape Town',
          description: 'Professional painting services for residential and commercial properties',
          address: 'Cape Town, Western Cape',
          priceRange: '$$',
          serviceArea: ['Cape Town', 'Southern Suburbs', 'Northern Suburbs', 'Atlantic Seaboard', 'City Bowl', 'West Coast']
        }),
        generateServiceSchema({
          name: 'Complete Painting Solutions',
          description: 'Residential, commercial, and industrial painting services',
          provider: 'Painters Cape Town',
          areaServed: ['Cape Town', 'Western Cape'],
          serviceType: ['Residential Painting', 'Commercial Painting', 'Industrial Painting', 'Roof Painting']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Painters Cape Town",
    location: "Cape Town, Western Cape",
    website: "https://painters-cape-town.co.za/",
    heroImage: "https://public.readdy.ai/ai/img_res/ed2058f6d0d6cc865654df02dc9bab65.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=stunning%20painted%20modern%20cape%20town%20home%20exterior%20with%20mountain%20views%20contemporary%20design%20professional%20finish%20manicured%20garden%20blue%20sky%20coastal%20architecture&width=800&height=600&seq=gallery-pct1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=commercial%20office%20building%20painting%20project%20cape%20town%20cbd%20high%20rise%20structure%20professional%20painters%20scaffolding%20modern%20glass%20facade%20urban%20skyline&width=800&height=600&seq=gallery-pct2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=elegant%20interior%20living%20space%20cape%20town%20home%20sophisticated%20neutral%20color%20palette%20modern%20furniture%20natural%20light%20professional%20painting%20finish&width=800&height=600&seq=gallery-pct3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=roof%20painting%20project%20cape%20town%20property%20with%20ocean%20views%20professional%20application%20weather%20resistant%20coating%20residential%20building%20coastal%20setting&width=800&height=600&seq=gallery-pct4&orientation=landscape"
    ],
    services: [
      {
        title: "Residential Painting Services",
        description: "Transform your home with our comprehensive residential painting services. We specialize in both interior and exterior painting for houses, apartments, townhouses, and luxury estates throughout Cape Town. Our experienced team understands the unique architectural styles found across the Mother City, from Victorian homes in the Southern Suburbs to modern apartments in the City Bowl and beachfront properties along the Atlantic Seaboard.",
        features: ["Complete interior painting all rooms", "Exterior facade and wall painting", "Heritage home restoration and preservation", "Modern apartment and condo painting", "Luxury estate painting services", "Color consultation and design advice", "Premium paint brand selection", "Wallpaper removal and surface preparation", "Ceiling and cornice painting", "Door and window frame painting"]
      },
      {
        title: "Commercial Painting Solutions",
        description: "Professional commercial painting services designed to enhance your business premises while minimizing operational disruption. We work with offices, retail stores, restaurants, hotels, shopping centers, and industrial facilities across Cape Town. Our team is experienced in managing large-scale commercial projects with strict timelines and quality standards, ensuring your business looks professional and inviting to customers and clients.",
        features: ["Office buildings and corporate spaces", "Retail stores and shopping centers", "Restaurants and hospitality venues", "Industrial warehouses and facilities", "Strata and body corporate projects", "High-rise building painting", "After-hours and weekend scheduling", "Minimal business disruption", "Safety compliance and insurance", "Project management and coordination"]
      },
      {
        title: "Specialized Painting Services",
        description: "Beyond standard painting, we offer specialized services including roof painting, waterproofing, damp proofing, and protective coatings. Cape Town's coastal climate requires expert knowledge of weather-resistant materials and application techniques. Our specialized services protect your property from the elements while enhancing its appearance and value.",
        features: ["Roof painting and restoration", "Waterproofing and damp proofing", "Heat-reflective roof coatings", "Anti-corrosion treatments", "Wood staining and varnishing", "Concrete sealing and coating", "Graffiti removal and prevention", "Texture coating applications", "Epoxy floor coatings", "Protective industrial coatings"]
      }
    ],
    serviceAreas: ["Cape Town CBD", "Southern Suburbs", "Northern Suburbs", "Atlantic Seaboard", "City Bowl", "Constantia", "Claremont", "Rondebosch", "Newlands", "Camps Bay", "Sea Point", "Green Point", "Table View", "Durbanville", "Bellville", "Somerset West", "Strand", "Hout Bay"],
    about: "Painters Cape Town is the leading professional painting company serving the greater Cape Town metropolitan area and surrounding regions. With over 20 years of industry experience and a team of highly skilled, certified painters, we have successfully completed thousands of residential, commercial, and industrial painting projects throughout the Western Cape. Our reputation is built on exceptional craftsmanship, reliability, competitive pricing, and unwavering commitment to customer satisfaction.",
    aboutExtended: [
      "Founded in 2003, our company has grown from a small local operation to become one of Cape Town's most trusted and respected painting contractors. We understand that Cape Town's unique coastal climate, diverse architectural heritage, and varying weather conditions require specialized knowledge and expertise. From the salt air of the Atlantic Seaboard to the hot summers of the Northern Suburbs, from historic Victorian homes to ultra-modern commercial developments, we have the experience and skills to handle any painting challenge.",
      "Our team consists of fully qualified, experienced painters who undergo continuous training in the latest painting techniques, safety procedures, and product applications. We are committed to using only premium quality paints and materials from leading international and local brands including Dulux, Plascon, Prominent Paints, and specialized coating manufacturers. Every product we use is carefully selected for its durability, environmental friendliness, and suitability for Cape Town's specific climate conditions.",
      "What truly distinguishes Painters Cape Town from other painting contractors is our comprehensive approach to project management and customer service. We believe that successful painting projects require more than just technical skill – they demand clear communication, meticulous planning, attention to detail, and respect for your property and schedule. From your initial consultation through to final completion and beyond, we maintain transparent communication, provide detailed written quotes, adhere to agreed timelines, and ensure your complete satisfaction with every aspect of our work.",
      "We are fully licensed, insured, and compliant with all relevant safety, environmental, and industry regulations. Our comprehensive insurance coverage protects both our team and your property throughout the project. We maintain strict safety protocols on all job sites, use environmentally responsible practices, and ensure proper disposal of all materials. Our commitment to professionalism extends to every aspect of our operations, from our uniformed team members to our clean, organized work practices and thorough post-project cleanup."
    ],
    ourApproach: [
      "Comprehensive on-site consultation and property assessment",
      "Detailed project planning and timeline development",
      "Professional color consultation and design recommendations",
      "Thorough surface preparation including repairs and priming",
      "Premium quality paints and materials selection",
      "Expert application techniques for superior finishes",
      "Strict quality control and inspection procedures",
      "Clean, organized, and respectful work practices",
      "Daily progress updates and communication",
      "Complete cleanup and waste removal",
      "Final walkthrough and quality inspection",
      "Comprehensive warranty and after-service support"
    ],
    whyChoose: [
      "20+ years of professional painting experience",
      "Thousands of successful projects completed",
      "Fully licensed, certified, and insured",
      "Experienced and qualified painting team",
      "Premium quality paints and materials",
      "Competitive and transparent pricing",
      "Free detailed quotes and consultations",
      "Comprehensive warranty coverage",
      "Excellent customer reviews and testimonials",
      "All Cape Town areas serviced",
      "Residential and commercial expertise",
      "Heritage restoration specialists",
      "Environmentally responsible practices",
      "Safety compliance guaranteed",
      "Project management excellence"
    ],
    testimonials: [
      {
        name: "Sarah Mitchell",
        rating: 5,
        text: "Painters Cape Town exceeded all our expectations! They painted our entire home in Constantia and the results are absolutely stunning. The team was professional, punctual, and incredibly detail-oriented. They provided excellent color advice and the finish is flawless. Highly recommend their services!"
      },
      {
        name: "David Naidoo",
        rating: 5,
        text: "We hired Painters Cape Town for our commercial office building in the CBD and couldn't be happier. They worked efficiently around our business hours, completed the project on schedule, and the quality is exceptional. Great communication throughout and very competitive pricing. Will definitely use them again!"
      },
      {
        name: "Lisa van der Merwe",
        rating: 5,
        text: "Outstanding service from start to finish! They painted the exterior of our home in Camps Bay and it looks absolutely beautiful. The team was respectful, clean, and professional. They handled everything from surface preparation to final touches with expertise. Worth every cent!"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Painters Cape Town</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Painters Cape Town</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <p className="text-gray-700 text-lg mb-4">We provide professional painting services throughout Cape Town and surrounding areas:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Professional Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">Comprehensive painting solutions for all types of properties throughout Cape Town.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">All Cape Town areas</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 7AM-6PM</p>
                    <p className="text-gray-600">Sat: 8AM-2PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Table View</li>
                <li>Melkbosstrand</li>
                <li>Durbanville</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://leaderr.co/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by leaderr
              </a>
              <span>•</span>
              <a href="https://seostudio.co.za/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                SEO by SEO Studio
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
